import React, { useEffect, useRef, useState } from "react";
import { Loader2, UserMinus } from "lucide-react";
import { participantName } from "../../backend/api/chat";
import type { ChatParticipant } from "../../backend/api/chat";
import {
  useAddParticipant,
  useRemoveParticipant,
} from "../../backend/queries/chat";
import { ConfirmDialog } from "../../components/patterns";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { PeoplePicker } from "./PeoplePicker";
import { TOUCH_CLOSE } from "./dialogClose";
import { useFocusReturn } from "./useFocusReturn";

type GroupMembersDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  participants: ChatParticipant[];
  currentUserId: string | null | undefined;
  /** A project chat: the API allows edits, but the project also moves people. */
  projectLinked: boolean;
};

/**
 * Staff-only flow for changing who is in an existing group.
 *
 * The page only offers it to staff on a group. The API refuses clients and
 * direct messages, and takes one person per add, so adding several people is
 * one request each.
 */
export function GroupMembersDialog({
  open,
  onOpenChange,
  conversationId,
  participants,
  currentUserId,
  projectLinked,
}: GroupMembersDialogProps) {
  const focusReturn = useFocusReturn(open);
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<ChatParticipant | null>(
    null,
  );
  const confirmFocusReturn = useFocusReturn(pendingRemoval !== null);
  const membersHeading = useRef<HTMLHeadingElement>(null);
  const removedSomeone = useRef(false);
  const add = useAddParticipant(conversationId);
  const remove = useRemoveParticipant(conversationId);

  useEffect(() => {
    if (open) return;
    setSelected([]);
    setError(null);
    setPendingRemoval(null);
  }, [open]);

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );

  const submitAdd = async () => {
    setError(null);
    const remaining = [...selected];
    try {
      for (const userId of selected) {
        await add.mutateAsync(userId);
        remaining.splice(remaining.indexOf(userId), 1);
      }
      setSelected([]);
    } catch (addError) {
      // Keep only the people who were not added, so a retry does not repeat.
      setSelected(remaining);
      setError(
        addError instanceof Error
          ? addError.message
          : "People could not be added.",
      );
    }
  };

  const confirmRemoval = async () => {
    if (!pendingRemoval) return;
    setError(null);
    try {
      await remove.mutateAsync(pendingRemoval.user_id);
      removedSomeone.current = true;
      setPendingRemoval(null);
      // Removing yourself ends your access, so the group has nothing to show.
      if (pendingRemoval.user_id === currentUserId) onOpenChange(false);
    } catch (removeError) {
      setPendingRemoval(null);
      setError(
        removeError instanceof Error
          ? removeError.message
          : "The person could not be removed.",
      );
    }
  };

  const removingSelf = pendingRemoval?.user_id === currentUserId;
  const removingName = pendingRemoval ? participantName(pendingRemoval) : "";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="grid-cols-[minmax(0,1fr)] sm:max-w-md"
          closeClassName={TOUCH_CLOSE} {...focusReturn}>
          <DialogHeader>
            <DialogTitle>Group members</DialogTitle>
            <DialogDescription>
              {projectLinked
                ? "This is a project chat. Its members follow the project, so a change made here can be undone the next time the project's people change."
                : "People you add see the whole history. People you remove lose access, and their earlier messages stay."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <section
              aria-labelledby="chat-members-current"
              className="space-y-1.5"
            >
              <h3
                id="chat-members-current"
                ref={membersHeading}
                tabIndex={-1}
                className="text-sm font-medium outline-none"
              >
                In this group ({participants.length})
              </h3>
              <ul className="max-h-40 divide-y overflow-y-auto rounded-md border">
                {participants.map((participant) => {
                  const name = participantName(participant);
                  return (
                    <li
                      key={participant.user_id}
                      className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
                    >
                      <span className="min-w-0">
                        <span className="block truncate">
                          {name}
                          {participant.user_id === currentUserId
                            ? " (you)"
                            : ""}
                        </span>
                        {participant.account_role ? (
                          <span className="block text-xs text-muted-foreground">
                            {participant.account_role}
                          </span>
                        ) : null}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="min-h-touch shrink-0 gap-1.5 text-xs md:min-h-0"
                        aria-label={`Remove ${name}`}
                        onClick={() => setPendingRemoval(participant)}
                      >
                        <UserMinus className="h-3.5 w-3.5" aria-hidden="true" />
                        Remove
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </section>

            <h3 className="text-sm font-medium">Add people</h3>
            <PeoplePicker
              enabled={open}
              selected={selected}
              onToggle={toggle}
              excludeIds={participants.map(
                (participant) => participant.user_id,
              )}
              emptyText="Everyone you can message is already in this group."
              idPrefix="chat-members"
            />

            {error ? (
              <p role="alert" className="text-sm text-tone-danger-foreground">
                {error}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="min-h-touch md:min-h-0"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            <Button
              className="min-h-touch md:min-h-0"
              onClick={() => void submitAdd()}
              disabled={!selected.length || add.isPending}
            >
              {add.isPending ? (
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin"
                  aria-hidden="true"
                />
              ) : null}
              {selected.length > 1
                ? `Add ${selected.length} people`
                : "Add person"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingRemoval !== null}
        onOpenChange={(next) => {
          if (!next) setPendingRemoval(null);
        }}
        title={removingSelf ? "Leave this group?" : `Remove ${removingName}?`}
        description={
          removingSelf
            ? "You will lose access to this group and its history. Your earlier messages stay for the others."
            : `${removingName} will lose access to this group straight away. Their earlier messages stay in the thread.`
        }
        confirmLabel={removingSelf ? "Leave group" : "Remove person"}
        tone="destructive"
        busy={remove.isPending}
        onCloseAutoFocus={(event) => {
          // After a removal the Remove button that opened this is gone, so put
          // focus on the member list. Otherwise return it to that button.
          if (!removedSomeone.current) {
            confirmFocusReturn.onCloseAutoFocus(event);
            return;
          }
          removedSomeone.current = false;
          event.preventDefault();
          membersHeading.current?.focus();
        }}
        onConfirm={() => void confirmRemoval()}
      />
    </>
  );
}
